import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterPill from "@/components/base/FilterPill";
import Pagination from "@/components/base/Pagination";
import WorkCard from "@/components/feature/WorkCard";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { worksList } from "@/mocks/works";

const typeOptions = ["全部", "电影", "电视剧", "综艺"];
const decadeOptions = [
  { key: "all", label: "全部年代" },
  { key: "2020", label: "2020 年代" },
  { key: "2010", label: "2010 年代" },
  { key: "2000", label: "2000 年代" },
  { key: "1990", label: "1990 年代" },
];
const sortOptions = [
  { key: "latest", label: "上映时间" },
  { key: "title", label: "作品名称" },
  { key: "hot", label: "热度" },
];
const PAGE_SIZE = 6;

export default function WorksListPage() {
  useDocumentMeta({
    title: "张柏芝影视作品大全 - 电影电视剧综艺资料",
    description:
      "收录张柏芝参演的电影、电视剧与综艺作品资料，包含上映年份、导演、角色介绍与剧情简介，支持按年代与类型筛选。",
    keywords: "张柏芝影视作品,张柏芝电影,张柏芝电视剧,张柏芝综艺,影视资料",
  });

  const [type, setType] = useState("全部");
  const [decade, setDecade] = useState("all");
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  const filtered = useMemo(() => {
    const items = worksList.filter((item) => {
      const matchesType = type === "全部" || item.type === type;
      const matchesDecade = decade === "all" || String(item.year).startsWith(decade.slice(0, 3));
      return matchesType && matchesDecade;
    });
    return [...items].sort((a, b) => {
      if (sort === "title") return a.title.localeCompare(b.title, "zh-Hans-CN");
      if (sort === "hot") return b.heat - a.heat;
      return b.year - a.year;
    });
  }, [type, decade, sort]);

  useEffect(() => {
    setPage(1);
  }, [type, decade, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const reset = () => {
    setType("全部");
    setDecade("all");
    setSort("latest");
  };

  return (
    <SiteLayout>
      <PageHero
        kicker="Filmography"
        title="影视作品"
        description="从一九九八年的第一部电影到即将上映的新片，按年代与类型整理的完整作品档案，包含角色介绍、主创信息与剧照。"
      >
        <Breadcrumb items={[{ label: "影视作品" }]} />
      </PageHero>

      <div className="w-full px-4 py-10 md:px-8 md:py-14 lg:px-12">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <button
            type="button"
            onClick={() => setFilterOpen((open) => !open)}
            className="flex w-full cursor-pointer items-center justify-between text-sm text-foreground-800 lg:hidden"
          >
            <span className="flex items-center gap-2">
              <i className="ri-filter-3-line text-primary-600" aria-hidden="true"></i>
              筛选与排序
            </span>
            <i className={filterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} aria-hidden="true"></i>
          </button>

          <div className={`${filterOpen ? "mt-4 block" : "hidden"} lg:block`}>
            <div className="flex flex-col gap-4 border-b border-background-200 pb-4 lg:flex-row lg:items-start lg:gap-8">
              <div className="flex items-start gap-3">
                <span className="w-16 shrink-0 pt-1.5 text-xs text-foreground-500">作品类型</span>
                <div className="flex flex-wrap gap-2">
                  {typeOptions.map((item) => (
                    <FilterPill key={item} label={item} active={type === item} onClick={() => setType(item)} />
                  ))}
                </div>
              </div>

              <div className="flex items-start gap-3">
                <span className="w-16 shrink-0 pt-1.5 text-xs text-foreground-500">年代</span>
                <div className="flex flex-wrap gap-2">
                  {decadeOptions.map((item) => (
                    <FilterPill
                      key={item.key}
                      label={item.label}
                      active={decade === item.key}
                      onClick={() => setDecade(item.key)}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <span className="text-xs text-foreground-500">排序</span>
                <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
                  {sortOptions.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setSort(item.key)}
                      aria-pressed={sort === item.key}
                      className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                        sort === item.key
                          ? "bg-background-50 text-primary-700"
                          : "text-foreground-600 hover:text-foreground-900"
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-xs text-foreground-500">
                  共 <span className="text-foreground-900">{filtered.length}</span> 部作品
                </span>
                <button
                  type="button"
                  onClick={reset}
                  className="cursor-pointer whitespace-nowrap text-xs text-foreground-600 transition-colors hover:text-primary-700"
                >
                  重置
                </button>
              </div>
            </div>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-20 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-film-line text-2xl" aria-hidden="true"></i>
            </span>
            <p className="mt-4 text-sm font-medium text-foreground-900">该组合下暂无作品</p>
            <p className="mt-2 max-w-sm text-xs leading-6 text-foreground-500">
              可以尝试切换作品类型或年代，也可以直接查看全部作品档案。
            </p>
            <button
              type="button"
              onClick={reset}
              className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs text-background-50 transition-colors hover:bg-primary-600"
            >
              查看全部作品
            </button>
          </div>
        ) : (
          <>
            <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4 xl:grid-cols-5">
              {current.map((item) => (
                <WorkCard key={item.id} item={item} />
              ))}
            </div>
            <Pagination
              page={page}
              totalPages={totalPages}
              onChange={(next) => {
                setPage(next);
                window.scrollTo({ top: 320, behavior: "smooth" });
              }}
              note={`每页 ${PAGE_SIZE} 部（前端演示分页）`}
            />
          </>
        )}

        <div className="mt-12 flex flex-col gap-4 rounded-lg bg-secondary-100 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-heading text-sm font-semibold text-foreground-950">按角色读作品</p>
            <p className="mt-1 text-xs leading-6 text-foreground-600">
              专题「镜头里的二十年」把九个重要角色重新排了一次序，从起点读到现在。
            </p>
          </div>
          <Link
            to="/special/s001"
            className="inline-flex w-fit cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs text-background-50 transition-colors hover:bg-primary-600"
          >
            进入专题
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
      </div>
    </SiteLayout>
  );
}